import { defineStore } from 'pinia'
import axios from '../config/api.js'
import { unwrapCollection, toNumber, LD_HEADERS } from './apiCollection.js'

// Taille de page fixe côté API Platform (paramètre `limit` ignoré par le serveur)
const API_PAGE_SIZE = 30

// Mappe un Produit API Platform (codes/descriptions SAP & Oracle) vers le modèle de la vue.
// Prix, stock, statut et image n'existent pas dans l'API : null => affiché « — ».
export function normalizeProduit(raw = {}) {
  const trim = (v) => (typeof v === 'string' ? v.trim() : v)
  return {
    ...raw,
    id: raw.id,
    name: trim(raw.description_sap || raw.descriptionSap || raw.description_oracle || raw.descriptionOracle || raw.name || ''),
    reference: trim(raw.codes_sap || raw.codesSap || raw.codes_oracle || raw.codesOracle || raw.reference || ''),
    codeOracle: raw.codes_oracle ?? raw.codesOracle ?? null,
    description: trim(raw.description_oracle || raw.descriptionOracle || raw.description || ''),
    category: trim(raw.item_category ?? raw.itemCategory ?? raw.category ?? '') || null,
    supplier: trim(raw.fournisseur ?? raw.supplier ?? '') || null,
    unit: raw.uom_sap ?? raw.uomSap ?? raw.unit ?? null,
    price: toNumber(raw.prix_HT ?? raw.prixHT ?? raw.price),
    costPrice: toNumber(raw.costPrice),
    stock: toNumber(raw.stock),
    minStock: toNumber(raw.minStock),
    status: raw.status ?? null,
    image: raw.image ?? null,
    isNew: raw.isNew ?? false
  }
}

export const useProduitsStore = defineStore('produits', {
  state: () => ({
    produits: [],
    loading: false,
    error: null,
    stats: {
      total: 0,
      active: 0,
      lowStock: 0,
      categories: 0
    },
    pagination: {
      page: 1,
      limit: API_PAGE_SIZE,
      total: 0
    },
    filters: {
      category: '',
      status: '',
      stock: '',
      supplier: '',
      search: ''
    }
  }),

  getters: {
    // Produits par statut
    activeProducts: (state) => state.produits.filter(p => p.status === 'active'),
    inactiveProducts: (state) => state.produits.filter(p => p.status === 'inactive'),
    discontinuedProducts: (state) => state.produits.filter(p => p.status === 'discontinued'),
    
    // Produits par stock
    inStockProducts: (state) => state.produits.filter(p => p.stock != null && p.stock > (p.minStock ?? 0)),
    lowStockProducts: (state) => state.produits.filter(p => p.stock != null && p.minStock != null && p.stock <= p.minStock && p.stock > 0),
    outOfStockProducts: (state) => state.produits.filter(p => p.stock === 0),
    
    // Produits par catégorie
    productsByCategory: (state) => {
      const categories = {}
      state.produits.forEach(produit => {
        if (!categories[produit.category]) {
          categories[produit.category] = []
        }
        categories[produit.category].push(produit)
      })
      return categories
    },
    
    // Produits par fournisseur
    productsBySupplier: (state) => {
      const suppliers = {}
      state.produits.forEach(produit => {
        const supplier = produit.supplier || 'Non défini'
        if (!suppliers[supplier]) {
          suppliers[supplier] = []
        }
        suppliers[supplier].push(produit)
      })
      return suppliers
    },
    
    // Valeur totale du stock
    totalStockValue: (state) => {
      return state.produits.reduce((total, produit) => {
        return total + (produit.stock * produit.price)
      }, 0)
    },
    
    // Valeur totale du coût
    totalCostValue: (state) => {
      return state.produits.reduce((total, produit) => {
        return total + (produit.stock * (produit.costPrice || 0))
      }, 0)
    },
    
    // Marge moyenne
    averageMargin: (state) => {
      const produitsWithCost = state.produits.filter(p => p.costPrice && p.costPrice > 0)
      if (produitsWithCost.length === 0) return 0
      
      const totalMargin = produitsWithCost.reduce((total, produit) => {
        const margin = ((produit.price - produit.costPrice) / produit.price) * 100
        return total + margin
      }, 0)
      
      return Math.round(totalMargin / produitsWithCost.length)
    },
    
    // Top produits par valeur de stock
    topProductsByValue: (state) => {
      return [...state.produits]
        .sort((a, b) => (b.stock * b.price) - (a.stock * a.price))
        .slice(0, 10)
    },
    
    // Produits récemment ajoutés
    recentProducts: (state) => {
      return [...state.produits]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 10)
    },
    
    // Catégories uniques
    uniqueCategories: (state) => {
      return [...new Set(state.produits.map(p => p.category).filter(Boolean))]
    },
    
    // Fournisseurs uniques
    uniqueSuppliers: (state) => {
      return [...new Set(state.produits.map(p => p.supplier).filter(Boolean))]
    }
  },

  actions: {
    // Charger les produits
    async loadProduits(params = {}) {
      this.loading = true
      this.error = null
      
      try {
        const response = await axios.get('/api/produits', {
          params: {
            page: this.pagination.page,
            limit: this.pagination.limit,
            ...params
          },
          headers: LD_HEADERS
        })
        
        const { items, totalItems } = unwrapCollection(response.data)
        this.produits = items.map(normalizeProduit)
        this.pagination.total = totalItems ?? ((this.pagination.page - 1) * this.pagination.limit + this.produits.length)
        this.updateStats()
        
      } catch (error) {
        this.error = 'Erreur lors du chargement des produits'
        console.error('Erreur loadProduits:', error)
        throw error
      } finally {
        this.loading = false
      }
    },
    
    // Créer un produit
    async createProduit(produitData) {
      try {
        const response = await axios.post('/api/produits', produitData)
        
        this.produits.unshift(normalizeProduit(response.data))
        this.updateStats()
        
        return response.data
      } catch (error) {
        this.error = 'Erreur lors de la création du produit'
        throw error
      }
    },
    
    // Mettre à jour un produit
    async updateProduit(id, produitData) {
      try {
        const response = await axios.put(`/api/produits/${id}`, produitData)
        
        const index = this.produits.findIndex(p => p.id === id)
        if (index !== -1) {
          this.produits[index] = normalizeProduit(response.data)
        }
        this.updateStats()
        
        return response.data
      } catch (error) {
        this.error = 'Erreur lors de la mise à jour du produit'
        throw error
      }
    },
    
    // Supprimer un produit
    async deleteProduit(id) {
      try {
        await axios.delete(`/api/produits/${id}`)
        
        this.produits = this.produits.filter(p => p.id !== id)
        this.updateStats()
        
      } catch (error) {
        this.error = 'Erreur lors de la suppression du produit'
        throw error
      }
    },
    
    // Synchroniser avec l'ERP
    async syncProduits() {
      try {
        const response = await axios.post('/api/produits/sync')
        
        // Recharger les produits après synchronisation
        await this.loadProduits()
        
        return response.data
      } catch (error) {
        this.error = 'Erreur lors de la synchronisation'
        throw error
      }
    },
    
    // Exporter les produits
    async exportProduits(format = 'csv', filters = {}) {
      try {
        const response = await axios.post('/api/produits/export', {
          format,
          filters
        }, {
          responseType: 'blob'
        })
        
        // Télécharger le fichier
        const blob = new Blob([response.data], { 
          type: response.headers['content-type'] || 'application/octet-stream' 
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `produits_${new Date().toISOString().split('T')[0]}.${format}`
        link.click()
        window.URL.revokeObjectURL(url)
        
      } catch (error) {
        this.error = 'Erreur lors de l\'export'
        throw error
      }
    },
    
    // Rechercher des produits
    async searchProduits(query) {
      this.filters.search = query
      await this.loadProduits({ search: query })
    },
    
    // Mettre à jour le stock
    async updateStock(id, newStock) {
      try {
        const response = await axios.patch(`/api/produits/${id}/stock`, {
          stock: newStock
        })
        
        const index = this.produits.findIndex(p => p.id === id)
        if (index !== -1) {
          this.produits[index].stock = newStock
        }
        this.updateStats()
        
        return response.data
      } catch (error) {
        this.error = 'Erreur lors de la mise à jour du stock'
        throw error
      }
    },
    
    // Mettre à jour les prix
    async updatePrices(updates) {
      try {
        const response = await axios.patch('/api/produits/prices', {
          updates
        })
        
        // Mettre à jour les produits localement
        updates.forEach(update => {
          const index = this.produits.findIndex(p => p.id === update.id)
          if (index !== -1) {
            this.produits[index].price = update.price
            if (update.costPrice !== undefined) {
              this.produits[index].costPrice = update.costPrice
            }
          }
        })
        
        return response.data
      } catch (error) {
        this.error = 'Erreur lors de la mise à jour des prix'
        throw error
      }
    },
    
    // Dupliquer un produit
    async duplicateProduit(id) {
      const produit = this.produits.find(p => p.id === id)
      if (!produit) throw new Error('Produit non trouvé')
      
      const duplicatedProduit = {
        ...produit,
        name: `${produit.name} (Copie)`,
        reference: `${produit.reference}-COPY`,
        stock: 0
      }
      
      delete duplicatedProduit.id
      delete duplicatedProduit.createdAt
      delete duplicatedProduit.updatedAt
      
      return await this.createProduit(duplicatedProduit)
    },
    
    // Mettre à jour les statistiques
    updateStats() {
      // null => pas de source API pour cet indicateur (affiché « — »)
      const has = (field) => this.produits.some(p => p[field] !== null && p[field] !== undefined)
      this.stats.total = this.pagination.total || this.produits.length
      this.stats.active = has('status') ? this.activeProducts.length : null
      this.stats.lowStock = has('stock') ? this.lowStockProducts.length : null
      this.stats.categories = this.uniqueCategories.length
    },
    
    // Définir la pagination
    setPagination(page, limit = null) {
      this.pagination.page = page
      if (limit) {
        this.pagination.limit = limit
      }
    },
    
    // Réinitialiser les filtres
    resetFilters() {
      this.filters = {
        category: '',
        status: '',
        stock: '',
        supplier: '',
        search: ''
      }
    },
    
    // Simulation d'appel API
    async simulateApiCall(url, data = {}, method = 'GET') {
      // Simuler un délai réseau
      await new Promise(resolve => setTimeout(resolve, Math.random() * 1000 + 500))
      
      // Simuler différentes réponses selon l'URL
      if (url === '/api/produits' && method === 'GET') {
        return {
          data: this.generateDemoProducts(),
          total: 150,
          page: data.page || 1,
          limit: data.limit || 20
        }
      }
      
      if (url === '/api/produits' && method === 'POST') {
        return {
          id: Date.now(),
          ...data,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      }
      
      if (url.includes('/api/produits/') && method === 'PUT') {
        return {
          ...data,
          updatedAt: new Date().toISOString()
        }
      }
      
      if (url === '/api/produits/sync' && method === 'POST') {
        return {
          synchronized: Math.floor(Math.random() * 10) + 1,
          updated: Math.floor(Math.random() * 5),
          created: Math.floor(Math.random() * 3)
        }
      }
      
      if (url === '/api/produits/export' && method === 'POST') {
        return {
          data: 'Nom,Référence,Prix,Stock\nProduit 1,REF001,100.00,50\nProduit 2,REF002,200.00,25',
          contentType: 'text/csv'
        }
      }
      
      return { success: true }
    },
    
    // Générer des produits de démonstration
    generateDemoProducts() {
      const categories = ['informatique', 'bureautique', 'mobilier', 'accessoires', 'consommables']
      const suppliers = ['dell', 'hp', 'lenovo', 'apple', 'microsoft']
      const statuses = ['active', 'inactive', 'discontinued']
      const units = ['piece', 'kg', 'liter', 'meter', 'pack']
      
      const products = []
      
      for (let i = 1; i <= 50; i++) {
        const category = categories[Math.floor(Math.random() * categories.length)]
        const supplier = suppliers[Math.floor(Math.random() * suppliers.length)]
        const status = statuses[Math.floor(Math.random() * statuses.length)]
        const unit = units[Math.floor(Math.random() * units.length)]
        
        const price = Math.round((Math.random() * 2000 + 50) * 100) / 100
        const costPrice = Math.round(price * (0.6 + Math.random() * 0.3) * 100) / 100
        const stock = Math.floor(Math.random() * 100)
        const minStock = Math.floor(Math.random() * 20) + 5
        
        products.push({
          id: i,
          name: `Produit ${i} - ${category}`,
          reference: `REF${String(i).padStart(3, '0')}`,
          description: `Description détaillée du produit ${i} dans la catégorie ${category}`,
          category,
          supplier,
          price,
          costPrice,
          stock,
          minStock,
          unit,
          status,
          image: Math.random() > 0.5 ? `https://picsum.photos/300/200?random=${i}` : null,
          specifications: `Spécifications techniques du produit ${i}\n- Caractéristique 1\n- Caractéristique 2\n- Caractéristique 3`,
          notes: Math.random() > 0.7 ? `Notes internes pour le produit ${i}` : '',
          isNew: Math.random() > 0.8,
          createdAt: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString(),
          updatedAt: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString()
        })
      }
      
      return products
    },
    
    // Charger les données de démonstration
    loadDemoData() {
      this.produits = this.generateDemoProducts()
      this.pagination.total = 150
      this.updateStats()
    }
  }
})